// =============================================================================
// VandieZ admin-web -- Push notifications (/notifications)
// =============================================================================
// Two tabs:
//   * Test  — pick one user, send a push. Used for FCM-token debugging
//             ("did the user actually get the push?") and one-off
//             ops touches ("hi, please re-submit your KYC").
//   * Broadcast — send a push to every active user matching a role +
//                 optional KYC tier. preference_key lets the admin pick
//                 which user opt-out gate applies (default opt-in).
//
// Backed by POST /admin/notifications/test + /broadcast. Both wrap the
// same fcm.send_to_user helper every other production push uses, so a
// successful test here proves the whole notification pipeline works
// for that user.
// =============================================================================

import { AlertCircle, BellRing, CheckCircle2, History, Loader2, Send, Users } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";

import { notificationsAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { UserPicker } from "@/components/UserPicker";
import { TableSkeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth";
import { useToast } from "@/lib/toast";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type { RecentNotificationRow } from "@/types/notifications";
import type { AdminUserSummary } from "@/types/rbac";
import type {
  AdminBroadcastResponse,
  AdminTestPushResponse,
} from "@/api/client";

type Tab = "test" | "broadcast" | "sent";

const ROLE_OPTIONS = ["", "driver", "shipper", "owner", "agent", "admin"];
const KYC_TIER_OPTIONS = ["", "phone_verified", "document_pending", "document_verified", "physically_verified"];
const PREFERENCE_KEYS = [
  { value: "", label: "(none — ignore opt-outs)" },
  { value: "marketing", label: "marketing (default opt-OUT)" },
  { value: "trip_status_changes", label: "trip_status_changes (default opt-in)" },
  { value: "bid_outcomes", label: "bid_outcomes (default opt-in)" },
  { value: "payment_events", label: "payment_events (default opt-in)" },
];

export function NotificationsPage(): React.JSX.Element {
  const [tab, setTab] = useState<Tab>("test");

  return (
    <div className="space-y-4">
      <PageHeader
        title="Push notifications"
        description="Send a test push to one user, or broadcast to every active user matching a role + KYC tier filter."
      />

      <div className="inline-flex gap-1 rounded-md border bg-background p-1">
        <button
          className={cn(
            "rounded px-3 py-1 text-sm",
            tab === "test"
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:bg-muted",
          )}
          onClick={() => { setTab("test"); }}
        >
          <BellRing className="mr-1 inline h-4 w-4" /> Test (one user)
        </button>
        <button
          className={cn(
            "rounded px-3 py-1 text-sm",
            tab === "broadcast"
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:bg-muted",
          )}
          onClick={() => { setTab("broadcast"); }}
        >
          <Users className="mr-1 inline h-4 w-4" /> Broadcast
        </button>
        <button
          className={cn(
            "rounded px-3 py-1 text-sm",
            tab === "sent"
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:bg-muted",
          )}
          onClick={() => { setTab("sent"); }}
        >
          <History className="mr-1 inline h-4 w-4" /> Sent
        </button>
      </div>

      {tab === "test" && <TestPushCard />}
      {tab === "broadcast" && <BroadcastCard />}
      {tab === "sent" && <SentCard />}
    </div>
  );
}

function TestPushCard(): React.JSX.Element {
  const { accessToken } = useAuth();
  const toast = useToast();
  const [target, setTarget] = useState<AdminUserSummary | null>(null);
  const [title, setTitle] = useState<string>("Test from VandieZ admin");
  const [body, setBody] = useState<string>("If you see this, your push setup works. No action required.");
  const [routePath, setRoutePath] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AdminTestPushResponse | null>(null);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (accessToken === null || target === null) {
      setError("Pick a user first.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await notificationsAdminApi.test(accessToken, {
        user_id: target.id,
        title: title.trim(),
        body: body.trim(),
        ...(routePath.trim().length > 0 ? { route_path: routePath.trim() } : {}),
      });
      setResult(res);
      if (res.success_count > 0) {
        toast.success(
          `Pushed to ${res.target_full_name} — ${res.success_count} delivered.`,
        );
      } else {
        toast.error(
          `No tokens delivered for ${res.target_full_name}. They may need to reopen the app.`,
        );
      }
    } catch (e: unknown) {
      const msg = e instanceof ApiError ? e.problem.detail : "Unexpected error.";
      setError(msg);
      toast.error(`Test push failed: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Test push (one user)</CardTitle>
        <CardDescription>
          Bypasses all user opt-out preferences — useful for verifying that
          FCM tokens are still valid. Tagged with <code>admin_test=true</code>{" "}
          in the payload so mobile suppresses any deep-link side effects.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1">
            <Label>Recipient</Label>
            <UserPicker
              value={target}
              onChange={setTarget}
              placeholder="Search by phone or name…"
              disabled={submitting}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="push-title">Title (max 120)</Label>
            <Input
              id="push-title"
              value={title}
              onChange={(e) => { setTitle(e.target.value); }}
              maxLength={120}
              disabled={submitting}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="push-body">Body (max 500)</Label>
            <Textarea
              id="push-body"
              value={body}
              onChange={(e) => { setBody(e.target.value); }}
              maxLength={500}
              rows={3}
              disabled={submitting}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="push-route">Deep link (optional)</Label>
            <Input
              id="push-route"
              value={routePath}
              onChange={(e) => { setRoutePath(e.target.value); }}
              placeholder="/trips/<id> · /kyc · /loads/<id>/bids"
              disabled={submitting}
            />
            <p className="text-xs text-muted-foreground">
              When set, the mobile push handler routes the user there on
              tap. Leave blank for a banner-only notification.
            </p>
          </div>
          {error !== null && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {result !== null && (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                Sent to {result.target_full_name}. Delivered{" "}
                <strong>{result.success_count}</strong>, failed{" "}
                <strong>{result.failure_count}</strong>, revoked{" "}
                <strong>{result.revoked_token_count}</strong>.
              </AlertDescription>
            </Alert>
          )}
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={submitting || target === null}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Send className="mr-1 h-4 w-4" />
                  Send test push
                </>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function BroadcastCard(): React.JSX.Element {
  const { accessToken } = useAuth();
  const toast = useToast();
  const [title, setTitle] = useState<string>("");
  const [body, setBody] = useState<string>("");
  const [primaryRole, setPrimaryRole] = useState<string>("driver");
  const [kycTier, setKycTier] = useState<string>("");
  const [routePath, setRoutePath] = useState<string>("");
  const [preferenceKey, setPreferenceKey] = useState<string>("marketing");
  const [confirming, setConfirming] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AdminBroadcastResponse | null>(null);
  const [preview, setPreview] = useState<{ audience_size: number } | null>(
    null,
  );
  const [previewing, setPreviewing] = useState<boolean>(false);

  const fetchPreview = async (): Promise<void> => {
    if (accessToken === null) {return;}
    setPreviewing(true);
    try {
      const res = await notificationsAdminApi.broadcastPreview(accessToken, {
        ...(primaryRole ? { primary_role: primaryRole } : {}),
        ...(kycTier ? { kyc_tier: kycTier } : {}),
      });
      setPreview({ audience_size: res.audience_size });
    } catch (e: unknown) {
      toast.error(
        e instanceof ApiError
          ? `Preview failed: ${e.problem.detail}`
          : "Preview failed.",
      );
    } finally {
      setPreviewing(false);
    }
  };

  const submit = async (): Promise<void> => {
    if (accessToken === null) {return;}
    if (title.trim().length === 0 || body.trim().length === 0) {
      setError("Title and body are required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await notificationsAdminApi.broadcast(accessToken, {
        title: title.trim(),
        body: body.trim(),
        ...(primaryRole ? { primary_role: primaryRole } : {}),
        ...(kycTier ? { kyc_tier: kycTier } : {}),
        ...(routePath.trim().length > 0 ? { route_path: routePath.trim() } : {}),
        ...(preferenceKey ? { preference_key: preferenceKey } : {}),
      });
      setResult(res);
      setConfirming(false);
      if (res.success_count > 0) {
        toast.success(
          `Broadcast: ${res.success_count} delivered across ${res.audience_size} users.`,
        );
      } else {
        toast.error("Broadcast hit 0 tokens. Audience may be inactive.");
      }
    } catch (e: unknown) {
      const msg = e instanceof ApiError ? e.problem.detail : "Unexpected error.";
      setError(msg);
      toast.error(`Broadcast failed: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Broadcast push</CardTitle>
        <CardDescription>
          Sends to every <code>active</code> user matching the filter. This is
          synchronous (slow for &gt;1000 users) — for large fan-outs queue a
          Celery job instead.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="bc-role">Primary role</Label>
              <select
                id="bc-role"
                value={primaryRole}
                onChange={(e) => { setPrimaryRole(e.target.value); }}
                disabled={submitting}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                {ROLE_OPTIONS.map((r) => (
                  <option key={r || "any"} value={r}>
                    {r || "(any — DANGER, everyone)"}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="bc-kyc">KYC tier (optional)</Label>
              <select
                id="bc-kyc"
                value={kycTier}
                onChange={(e) => { setKycTier(e.target.value); }}
                disabled={submitting}
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                {KYC_TIER_OPTIONS.map((t) => (
                  <option key={t || "any"} value={t}>
                    {t || "(any)"}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="bc-title">Title (max 120)</Label>
            <Input
              id="bc-title"
              value={title}
              onChange={(e) => { setTitle(e.target.value); }}
              maxLength={120}
              disabled={submitting}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="bc-body">Body (max 500)</Label>
            <Textarea
              id="bc-body"
              value={body}
              onChange={(e) => { setBody(e.target.value); }}
              maxLength={500}
              rows={3}
              disabled={submitting}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="bc-route">Deep link (optional)</Label>
            <Input
              id="bc-route"
              value={routePath}
              onChange={(e) => { setRoutePath(e.target.value); }}
              placeholder="/promotions/diwali · /kyc"
              disabled={submitting}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="bc-pref">Opt-out preference key</Label>
            <select
              id="bc-pref"
              value={preferenceKey}
              onChange={(e) => { setPreferenceKey(e.target.value); }}
              disabled={submitting}
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              {PREFERENCE_KEYS.map((p) => (
                <option key={p.value || "none"} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              Users with this preference explicitly set to false are
              skipped. Marketing defaults to OFF — most legacy rows
              won't receive marketing broadcasts unless they explicitly
              opted in.
            </p>
          </div>
          {error !== null && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {result !== null && (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                Audience: <strong>{result.audience_size}</strong>. Delivered{" "}
                <strong>{result.success_count}</strong>, failed{" "}
                <strong>{result.failure_count}</strong>, revoked tokens{" "}
                <strong>{result.revoked_token_count}</strong>.
              </AlertDescription>
            </Alert>
          )}
          <div className="flex justify-end gap-2">
            {confirming ? (
              <>
                <span className="self-center text-xs text-muted-foreground">
                  Send to every active{" "}
                  <strong>{primaryRole || "user"}</strong>?
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => { setConfirming(false); }}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => void submit()}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                      Broadcasting…
                    </>
                  ) : (
                    <>
                      <Send className="mr-1 h-4 w-4" />
                      Confirm broadcast
                    </>
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void fetchPreview()}
                  disabled={previewing}
                >
                  {previewing ? "Counting…" : "Preview audience"}
                </Button>
                {preview !== null && (
                  <span className="self-center text-sm text-muted-foreground">
                    Will reach{" "}
                    <strong className="text-foreground">
                      {preview.audience_size.toLocaleString("en-IN")}
                    </strong>{" "}
                    user{preview.audience_size === 1 ? "" : "s"}.
                  </span>
                )}
                <Button
                  type="button"
                  onClick={() => { setConfirming(true); }}
                  disabled={submitting || title.trim().length === 0 || body.trim().length === 0}
                >
                  <Send className="mr-1 h-4 w-4" />
                  Broadcast
                </Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}


/// What actually went out.
///
/// The other two tabs DISPATCH pushes. Until this existed there was no
/// read-back anywhere in the product, so a broadcast that reached nobody
/// looked exactly like one that worked -- which is not hypothetical
/// here: every push had no recipient for months because no client had
/// ever registered an FCM token, and nothing on this page would have
/// shown it.
function SentCard(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [rows, setRows] = useState<RecentNotificationRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (accessToken === null) {return;}
      try {
        const res = await notificationsAdminApi.recent(
          accessToken,
          { limit: 100 },
          signal,
        );
        setRows(res.rows);
        setError(null);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {return;}
        setError(
          e instanceof ApiError
            ? e.problem.detail
            : "Couldn't load sent notifications.",
        );
      } finally {
        setLoading(false);
      }
    },
    [accessToken],
  );

  useEffect(() => {
    const c = new AbortController();
    void load(c.signal);
    return () => { c.abort(); };
  }, [load]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Recently sent</CardTitle>
        <CardDescription>
          The last 100 notification rows, newest first. A row exists per
          recipient, so one broadcast appears many times.
          <span className="ml-1 text-foreground">
            {rows.length} shown.
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent>
        {error !== null && (
          <Alert variant="destructive" className="mb-3">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading && rows.length === 0 ? (
          <TableSkeleton rows={5} columns={3} />
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground text-sm">
            Nothing has been sent yet.
          </div>
        ) : (
          <ul className="divide-y">
            {rows.map((r) => (
              <li key={r.id} className="px-2 py-3">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-medium text-sm">{r.title}</span>
                  <span className="shrink-0 text-muted-foreground text-xs">
                    {new Date(r.created_at).toLocaleString()}
                  </span>
                </div>
                <p className="mt-0.5 text-muted-foreground text-sm">
                  {r.body}
                </p>
                <div className="mt-1 flex items-center gap-2 text-xs">
                  {r.event_type !== null && (
                    <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-muted-foreground">
                      {r.event_type}
                    </span>
                  )}
                  <span
                    className={
                      r.read_at === null
                        ? "text-muted-foreground"
                        : "text-success-subtle-foreground"
                    }
                  >
                    {r.read_at === null ? "unread" : "read"}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
